import React, { useId } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./button";
import { getPaginationItems, getPaginationState } from "../../utils/pagination";

/** Controlled footer: the caller owns fetching, sorting, filtering and page state. */
export default function PaginationFooter({
  page,
  pageSize,
  totalItems,
  onPageChange,
  disabled = false,
  ariaLabel = "Pagination",
  pageSizeOptions,
  onPageSizeChange,
  summaryText,
}) {
  const sizeId = useId();
  const { safePage, totalPages, startItem, endItem } = getPaginationState({
    page,
    pageSize,
    totalItems,
  });
  if (totalItems === 0) return null;

  const changePage = (nextPage) => {
    if (!disabled && nextPage !== safePage && nextPage >= 1 && nextPage <= totalPages) {
      onPageChange(nextPage);
    }
  };
  const canChangeSize = pageSizeOptions?.length > 0 && typeof onPageSizeChange === "function";
  const previousDisabled = disabled || safePage === 1;
  const nextDisabled = disabled || safePage === totalPages;

  return (
    <div className="@container mt-auto shrink-0 border-t border-workspace bg-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-5 gap-y-3">
          <p className="text-sm text-subtle">
            {summaryText ?? (
              <>
                Affichage de <span className="font-semibold text-primary">{startItem}</span> à{" "}
                <span className="font-semibold text-primary">{endItem}</span> sur{" "}
                <span className="font-semibold text-primary">{totalItems}</span> résultats
              </>
            )}
          </p>
          {canChangeSize && (
            <div className="flex items-center gap-2 text-sm text-subtle">
              <label htmlFor={sizeId}>Lignes par page :</label>
              <select
                id={sizeId}
                value={pageSize}
                disabled={disabled}
                onChange={(event) => {
                  const nextSize = Number(event.target.value);
                  if (!disabled && nextSize !== pageSize && pageSizeOptions.includes(nextSize)) {
                    onPageSizeChange(nextSize);
                  }
                }}
                className="min-h-11 rounded-md border border-workspace bg-surface px-3 text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hp-color-focus)] disabled:opacity-50"
              >
                {pageSizeOptions.map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
            </div>
          )}
        </div>
        <nav aria-label={ariaLabel} aria-busy={disabled} className="flex flex-1 basis-full flex-wrap justify-end gap-2 min-[640px]:basis-auto">
          <div className="flex w-full justify-between gap-3 @[28rem]:hidden">
            <Button type="button" variant="outline" disabled={previousDisabled} onClick={() => changePage(safePage - 1)}>
              Précédent
            </Button>
            <Button type="button" variant="outline" disabled={nextDisabled} onClick={() => changePage(safePage + 1)}>
              Suivant
            </Button>
          </div>
          <div className="hidden isolate -space-x-px rounded-md shadow-sm @[28rem]:inline-flex">
            <Button
              type="button"
              variant="outline"
              aria-label="Page précédente"
              disabled={previousDisabled}
              onClick={() => changePage(safePage - 1)}
              className="rounded-r-none px-3"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            {getPaginationItems(safePage, totalPages).map((item) =>
              typeof item === "number" ? (
                <Button
                  key={item}
                  type="button"
                  variant={item === safePage ? "accent" : "outline"}
                  aria-label={`Aller à la page ${item}`}
                  aria-current={item === safePage ? "page" : undefined}
                  disabled={disabled}
                  onClick={() => changePage(item)}
                  className="min-w-10 rounded-none px-3"
                >
                  {item}
                </Button>
              ) : (
                <span key={item} aria-hidden="true" className="inline-flex min-w-8 items-center justify-center border border-workspace bg-surface px-2 text-subtle">…</span>
              )
            )}
            <Button
              type="button"
              variant="outline"
              aria-label="Page suivante"
              disabled={nextDisabled}
              onClick={() => changePage(safePage + 1)}
              className="rounded-l-none px-3"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </nav>
      </div>
    </div>
  );
}
